import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Menu } from 'antd';
import {
  UserOutlined,
  KeyOutlined,
  ClusterOutlined,
  GiftOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
  BookOutlined,
  MenuOutlined,
  LogoutOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useStore } from '../../../store/useStore';
import { userInitial } from '../../../utils/userInitial';
import { HomeLink } from '../../../components/HomeLink';

const MENU_ITEMS = [
  { key: '/devops/creator-outreach/users', icon: <UserOutlined />, label: '用户管理' },
  { key: '/devops/creator-outreach/permissions', icon: <KeyOutlined />, label: '权限管理' },
  { key: '/devops/creator-outreach/permission-groups', icon: <ClusterOutlined />, label: '权限组管理' },
  { key: '/devops/creator-outreach/packages', icon: <GiftOutlined />, label: '套餐管理' },
  { key: '/devops/creator-outreach/benefits', icon: <SafetyCertificateOutlined />, label: '单独权益' },
  { key: '/devops/creator-outreach/orders', icon: <FileTextOutlined />, label: '订单管理' },
  { key: '/devops/creator-outreach/dicts', icon: <BookOutlined />, label: '字典管理' },
  { key: '/devops/creator-outreach/menus', icon: <MenuOutlined />, label: '菜单管理' }
];

const PAGE_META: Record<string, { title: string; subtitle?: string }> = {
  '/devops/creator-outreach/users': {
    title: '用户管理',
    subtitle: '邮箱 / 注册渠道 / 占用 · 不改变套餐额度'
  },
  '/devops/creator-outreach/permissions': {
    title: '权限管理',
    subtitle: '功能点与接口权限配置'
  },
  '/devops/creator-outreach/permission-groups': {
    title: '权限组管理',
    subtitle: '权限分组与套餐绑定'
  },
  '/devops/creator-outreach/packages': {
    title: '套餐管理',
    subtitle: '权限组 · 有效期 · 版本 · 邮箱 · 价格 · 店铺'
  },
  '/devops/creator-outreach/packages/new': {
    title: '新增套餐',
    subtitle: '配置价格、功能描述、权限与额度'
  },
  '/devops/creator-outreach/packages/edit': {
    title: '编辑套餐',
    subtitle: '配置价格、功能描述、权限与额度'
  },
  '/devops/creator-outreach/benefits': {
    title: '单独权益',
    subtitle: '店铺 / 邮件次卡权益配置与赠送'
  },
  '/devops/creator-outreach/benefits/new': {
    title: '单独权益',
    subtitle: '新增或编辑单独权益'
  },
  '/devops/creator-outreach/orders': {
    title: '订单管理',
    subtitle: '购买 / 升级 / 加购订单'
  },
  '/devops/creator-outreach/dicts': {
    title: '字典管理',
    subtitle: '系统枚举与配置字典'
  },
  '/devops/creator-outreach/menus': {
    title: '菜单管理',
    subtitle: '后台导航与菜单配置'
  }
};

export const AdminLayout = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminLoginStore } = useStore();
  const [selectedKeys, setSelectedKeys] = useState(['/devops/creator-outreach/users']);

  useEffect(() => {
    const matched =
      MENU_ITEMS.find((item) => item.key === location.pathname) ??
      MENU_ITEMS.find((item) => location.pathname.startsWith(`${item.key}/`));
    if (matched) {
      setSelectedKeys([matched.key]);
    }
  }, [location.pathname]);

  const meta = useMemo(() => {
    if (/^\/devops\/creator-outreach\/packages\/[0-9]{21}\/edit$/.test(location.pathname)) {
      return PAGE_META['/devops/creator-outreach/packages/edit'];
    }
    return (
      PAGE_META[location.pathname] ??
      PAGE_META[selectedKeys[0] ?? '/devops/creator-outreach/users'] ?? { title: 'Wowbit Ops' }
    );
  }, [location.pathname, selectedKeys]);

  const displayName = adminLoginStore.data.userinfo?.userName?.trim() || '运营管理员';
  const roleLabel = adminLoginStore.data.userinfo?.roleLabel || '运营管理员';

  const logout = async () => {
    await adminLoginStore.logout();
    navigate('/devops/creator-outreach/login', { replace: true });
  };

  return (
    <Layout className='admin-shell'>
      <Layout.Sider width={232} className='admin-sider' theme='dark'>
        <div className='admin-sider-brand'>
          <span className='admin-sider-logo' aria-hidden>
            <AppstoreOutlined />
          </span>
          <div className='admin-sider-brand-text'>
            <p className='admin-sider-brand-title'>后台管理</p>
            <p className='admin-sider-brand-sub'>Wowbit 达人建联</p>
          </div>
        </div>
        <Menu
          theme='dark'
          mode='inline'
          selectedKeys={selectedKeys}
          items={MENU_ITEMS}
          className='admin-sider-menu'
          onClick={({ key }) => navigate(key)}
        />
      </Layout.Sider>
      <Layout className='admin-main'>
        <header className='admin-header'>
          <div className='admin-header-title-wrap'>
            <h1 className='admin-header-title'>{meta.title}</h1>
          </div>
          <div className='admin-header-user'>
            <HomeLink />
            <div className='admin-header-user-meta'>
              <span className='admin-header-user-name'>{displayName}</span>
              <span className='admin-header-user-role'>{roleLabel}</span>
            </div>
            <span className='admin-header-avatar' aria-hidden>
              {userInitial(displayName)}
            </span>
            <button type='button' className='admin-header-logout' onClick={logout} title='退出登录'>
              <LogoutOutlined />
            </button>
          </div>
        </header>
        <Layout.Content className='admin-content'>
          <Outlet />
        </Layout.Content>
      </Layout>
    </Layout>
  );
});
